import React, { SyntheticEvent } from 'react';
import {
  Dropdown as SemanticDropdown,
  DropdownProps as SemanticDropdownProps,
  DropdownItemProps,
} from 'semantic-ui-react';
import { css } from '@emotion/react';
import tw from 'twin.macro';
import DangerouslySetInnerHTML from '../../EditableInput/DangerouslySetInnerHTML';
import { HtmlSpanProps, SemanticShorthandItem } from 'semantic-ui-react/dist/commonjs/generic';

export const searchPrefix: (options: DropdownItemProps[], value: string) => DropdownItemProps[] = (
  options,
  value
) => {
  return options.filter((o) => (o.text as string).toLowerCase().startsWith(value.toLowerCase()));
};
export interface DropdownProps extends Omit<SemanticDropdownProps, 'onChange' | 'options'> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (v: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputRef?: React.LegacyRef<React.Component<SemanticDropdownProps, any, any>>;
  id?: string;
  multiple?: boolean;
  clearable?: boolean;
  className?: string;
  buttonIcon?: JSX.Element;
  onClickButton?: (e: SyntheticEvent) => void;
  options?: (Omit<DropdownItemProps, 'description'> & {
    description?: string | SemanticShorthandItem<HtmlSpanProps>;
  })[];
}

export interface SearchInputProps extends DropdownProps {
  autoComplete?: string;
  type?: string;
}

export const DropdownStyling = [
  tw`rounded-input! border-color-input-field`,
  css`
    white-space: nowrap;
    .item {
      display: flex !important;
      align-items: center !important;
    }
    .ewebinar__powered_by_dropdown_item {
      opacity: 1 !important;
      @media (min-width: 768px) {
        display: none !important;
      }
    }
    .ewebinar__powered_by_dropdown_item_hidden {
      display: none !important;
    }
    .ewebinar__powered_by_dropdown_item:hover {
      background: white !important;
    }
  `,
];

const Dropdown: React.FC<DropdownProps> = ({
  onChange,
  inputRef,
  children,
  buttonIcon,
  onClickButton,
  placeholder = 'Select item',
  multiple,
  options,
  basic,
  searchInput,
  ...props
}) => {
  const currentOption = options?.find((o) => o.value === props.value);
  const text = currentOption?.text ?? undefined;

  const renderedOptions = React.useMemo(() => {
    if (options?.[0]?.description) {
      return options?.map((o) => ({
        search: `${o.text} ${o.description}`,
        key: o.key,
        value: o.value,
        text: o.text,
        content: (
          <div
            className='flex flex-col justify-start w-full text-sm'
            css={css`
              a {
                ${tw`text-color-brand`}
                text-decoration: underline;
              }
            `}
          >
            <div className='flex flex-col items-start mb-1/4e'>
              <div className='text-medium text-md'>{o.text}</div>
            </div>
            {typeof o.description === 'string' ? (
              <DangerouslySetInnerHTML
                className='mt-1/2e text-gray-600 whitespace-normal'
                markdownString={(o.description as string) ?? ''}
              />
            ) : (
              <div className='mt-1/2e text-gray-600 whitespace-normal'>
                {o.description as React.ReactNode}
              </div>
            )}
          </div>
        ),
      }));
    }

    return options;
  }, [options]);

  const selectedOption = options?.find((o) => o.value === props.value);

  const dropDown = React.useMemo(() => {
    return (
      <>
        <SemanticDropdown
          ref={inputRef}
          selection={Boolean(children) ? undefined : !basic}
          fluid
          selectOnBlur={false}
          placeholder={placeholder}
          {...props}
          className={`
            ${Boolean(children) && !Boolean(props.trigger) ? 'selection ' : ''}
            ${props.className ?? ''}
          `}
          basic={basic}
          multiple={multiple}
          options={renderedOptions}
          text={props.trigger ? undefined : text}
          onChange={(_, { value }): void => onChange && onChange(value)}
          css={[
            basic
              ? css`
                  .menu {
                    min-width: 100% !important;
                  }
                `
              : undefined,
            options?.[0]?.description
              ? css`
                  .item {
                    ${tw`border-b border-gray-200!`}
                    border-bottom-style: solid !important;
                  }
                `
              : '',
            ...DropdownStyling,
            buttonIcon
              ? css`
                  border-top-left-radius: 0 !important;
                  border-bottom-left-radius: 0 !important;
                  border-left: none !important;
                `
              : '',
          ]}
          onSearchChange={props.onSearchChange}
          searchInput={{
            ...(searchInput ?? {}),
            autoComplete: 'off',
          }}
        >
          {children}
        </SemanticDropdown>
        {searchInput?.autoComplete && (
          <label htmlFor={searchInput.autoComplete}>
            <input
              className='absolute w-0 h-0 opacity-0 pointer-events-none'
              id={searchInput.autoComplete}
              autoComplete={searchInput.autoComplete}
              name={searchInput.autoComplete}
              value={selectedOption?.text}
              tabIndex={-1}
              onChange={(e) => {
                const textValue = e.target.value;
                const value = renderedOptions?.find((o) => o.text === textValue)?.value;
                if (!value) return;
                onChange?.(value);
              }}
            />
          </label>
        )}
      </>
    );
  }, [
    basic,
    buttonIcon,
    children,
    inputRef,
    multiple,
    onChange,
    options,
    placeholder,
    props,
    renderedOptions,
    selectedOption?.text,
    text,
    searchInput,
  ]);

  if (!buttonIcon) {
    return dropDown;
  }

  return (
    <div className='flex flex-inline'>
      <button
        className={props.loading ? 'loading' : ''}
        css={css`
          ${tw`rounded-input border-color-input-box!`};
          background-color: #e2e8f0 !important;
          padding: 0 0.75rem;
          border: 1px solid;
          border-top-right-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
          border-right: none !important;

          &.loading > * {
            animation: icon-loading 2s linear infinite;
          }
        `}
        onClick={(e): void => {
          e.preventDefault();
          onClickButton && onClickButton(e);
        }}
      >
        {buttonIcon}
      </button>

      {dropDown}
    </div>
  );
};

export default Dropdown;
